import { MASTER_PAGE_ID, RuleId } from '@wix/a11y-rules';
import { DEFAULT_BRANCH_ID } from '@wix/a11y-wizard-common';
import { RuleAction } from '@wix/ambassador-promote-accessibility-v1-accessibility-report/types';
import { Badge, Box, Button, Checkbox, Loader, Text, TextButton } from '@wix/design-system';
import { Trans, useErrorHandler, useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus } from '@wix/rules-panel-ui';
import React, { useCallback, useState } from 'react';
import { BiCtaNames } from '../../../../../bi/types';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../../../../hooks/use-open-article-by-editor-host';
import { useSessionInfoContext } from '../../../../../hooks/use-session-info-context';
import { PanoramaErrorNames } from '../../../../../panorama/constants';
import { usePanoramaFunctions } from '../../../../../panorama/use-panorama-functions';
import { useRuleListDataMutation } from '../../../../../queries/use-rule-list-data-mutation';
import { useGlobalSectionState } from '../../../../../rule-mappers/shared/use-global-section-state';
import { showSaveErrorModal } from '../../../../modals/show-save-error-modal';
import { ColorContrastIncompleteElementTimeline } from './color-contrast-incomplete-element-timeline';
export const ColorContrastIncompleteElementContent = _ref => {
  let {
    locale,
    minimalRatio,
    elementIndex,
    status,
    failedElement,
    onClickCancel,
    onClickApply,
    pageId,
    showLoader
  } = _ref;
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    editorHost,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    isLoading,
    mutateAsync
  } = useRuleListDataMutation();
  const {
    elementId,
    attributeId
  } = failedElement;
  const {
    getResolvedError
  } = useErrorHandler();
  const {
    reportErrorPanorama
  } = usePanoramaFunctions();
  const {
    getIsComponentInGlobalSection
  } = useGlobalSectionState();
  const {
    experiments
  } = useExperiments();
  const enableGlobalSectionForIncompleteElements = experiments.enabled('specs.a11y.enableGlobalSectionForIncompleteElements');
  const {
    branchId
  } = useSessionInfoContext();
  const handleOnClick = useCallback(async () => {
    try {
      await mutateAsync({
        branchId: branchId || DEFAULT_BRANCH_ID,
        delete: failedElement.isValid,
        elementId: elementId + (attributeId ?? ''),
        pageId: enableGlobalSectionForIncompleteElements && getIsComponentInGlobalSection(elementId) ? MASTER_PAGE_ID : pageId,
        ruleAction: RuleAction.SKIPPED,
        ruleId: RuleId.COLOR_CONTRAST
      });
      onClickApply({
        currentContrastRatio: minimalRatio,
        element: failedElement,
        elementIndex,
        isIncomplete: true,
        isValid: !failedElement.isValid
      });
    } catch (error) {
      reportErrorPanorama(error, PanoramaErrorNames.suppressIncompleteElement);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        errorMessage: getResolvedError(error).message,
        handleTryAgain: () => {
          handleOnClick();
        },
        t
      });
    }
  }, [mutateAsync, branchId, failedElement, elementId, attributeId, enableGlobalSectionForIncompleteElements, getIsComponentInGlobalSection, pageId, onClickApply, minimalRatio, elementIndex, reportErrorPanorama, a11yWizardAdapter, biLogger, editorHost, getResolvedError, t]);
  const [isChecked, setIsChecked] = useState(failedElement.isValid);
  const isDisabled = isLoading || showLoader || isChecked === failedElement.isValid;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      learnMore: /*#__PURE__*/React.createElement(TextButtonComp, {
        onClick: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-incomplete-components-help');
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.LearnMore,
            index: elementIndex,
            status: status ? ElementStatus.ManualPassed : ElementStatus.ManualFailed
          });
        }
      }),
      text: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "thin",
        secondary: true
      })
    },
    i18nKey: "accessibility-wizard.rules-list.color-contrast.element.content.incomplete"
  })), /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "B60",
    borderRadius: "6px",
    direction: "vertical",
    gap: "SP2",
    padding: "SP2 SP2 SP3 SP2"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(Badge, {
    noPadding: true,
    showTooltip: false,
    size: "small",
    skin: "neutralStandard",
    type: "solid",
    uppercase: false
  }, /*#__PURE__*/React.createElement(Box, {
    padding: "0 4px"
  }, t('accessibility-wizard.rules-list.content.incomplete.badge')))), /*#__PURE__*/React.createElement(ColorContrastIncompleteElementTimeline, {
    elementIndex: elementIndex,
    locale: locale,
    minimalRatio: minimalRatio,
    status: status
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP3"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Checkbox, {
    checked: isChecked,
    size: "small",
    vAlign: "top",
    onChange: () => setIsChecked(!isChecked)
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true
  }, t('accessibility-wizard.rules-list.content.incomplete.checkbox')))), /*#__PURE__*/React.createElement(Box, {
    align: "right",
    gap: "9px"
  }, /*#__PURE__*/React.createElement(Button, {
    priority: "secondary",
    size: "tiny",
    onClick: onClickCancel
  }, t('accessibility-wizard.rules-list.content.incomplete.button.cancel')), /*#__PURE__*/React.createElement(Button, {
    disabled: isDisabled,
    size: "tiny",
    suffixIcon: isLoading || showLoader ? /*#__PURE__*/React.createElement(Box, {
      margin: "-2px 0 0 4px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : undefined,
    onClick: handleOnClick
  }, t('accessibility-wizard.rules-list.content.incomplete.button.confirm'))))));
};
export const TextButtonComp = _ref2 => {
  let {
    onClick,
    bold,
    children
  } = _ref2;
  return /*#__PURE__*/React.createElement(TextButton, {
    as: "a",
    size: "tiny",
    skin: "dark",
    target: "_blank",
    underline: "always",
    weight: bold ? 'bold' : 'normal',
    onClick: onClick
  }, children);
};
//# sourceMappingURL=color-contrast-incomplete-element-content.js.map